import { Cross1Icon } from '@radix-ui/react-icons';
import { Download1Icon, File6Icon, FilePlus1Icon } from 'icons';
import { RadioButton, ModalWindow, Loader } from 'components';
import {
  AttachmentsEmpty,
  AttachmentsHeader,
  AttachmentsPanel,
  AttachmentsSectionLabel,
  AttachmentsSummary,
  AttachmentsTitle,
  ButtonAddImage,
  ButtonCustom,
  CellModal,
  ContainerModal,
  ContentDate,
  DateLabel,
  InputModal,
  LabelModal,
  ModalTitle,
  DocumentAction,
  DocumentIconTile,
  DocumentInfo,
  DocumentItem,
  DocumentList,
  DocumentMeta,
  DocumentName,
  DocumentsCount,
  StyledCalendarIcon,
  Overlay,
} from './styles';
import NewCarousel from '~/components/NewCarousel';
import { Flex } from 'reflexbox';
import { SingleDatePicker } from 'react-dates';
import { colors } from 'styles/colors';
import { useTranslation } from 'react-i18next';
import { ChangeEvent } from 'react';
import { documentTypeLabel } from '~/helpers/file';

interface FeedbackModalProps {
  isLoading: boolean;
  state: any;
  maintenance: boolean | null;
  setMaintenance: (value: boolean) => void;
  images: Array<{ preview: string }>;
  pdfs: Array<{ preview: string; name: string; contentType?: string }>;
  handleModalClose: () => void;
  handleImageChange: (e: ChangeEvent<HTMLInputElement>) => void;
  removeImage: (index: number) => void;
  removePdf: (index: number) => void;
  formatPhone: (value: string) => string;
  submitFeedback: () => void;
  setState: (value: any) => void;
  render: () => void;
}

export function FeedbackModal({
  isLoading,
  state,
  maintenance,
  setMaintenance,
  images,
  pdfs,
  handleModalClose,
  handleImageChange,
  removeImage,
  removePdf,
  formatPhone,
  submitFeedback,
  setState,
  render,
}: Readonly<FeedbackModalProps>): JSX.Element {
  const { t } = useTranslation();
  const attachmentsSummary = [
    images.length > 0 ? `${images.length} ${t(images.length === 1 ? 'foto' : 'fotos').toLowerCase()}` : null,
    pdfs.length > 0 ? `${pdfs.length} ${t(pdfs.length === 1 ? 'documento' : 'documentos').toLowerCase()}` : null,
  ].filter(Boolean).join(' · ');
  const isDesktop = window.matchMedia('(min-width: 1024px)').matches;

  // Link direto, no próprio clique: o fetch dependia de CORS no bucket e o window.open de
  // fallback era bloqueado como popup. O storage já serve o arquivo como attachment.
  const downloadPdf = (url: string, name: string) => {
    const link = document.createElement('a');
    link.href = url;
    link.download = name;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.click();
  };

  const documentList = (
    <DocumentList>
      {pdfs.map((pdf, index) => (
        <DocumentItem key={pdf.preview}>
          <DocumentIconTile aria-hidden>
            <File6Icon size="20" color={colors.brand.primary.base} />
          </DocumentIconTile>
          <DocumentInfo type="button" title={pdf.name} onClick={() => downloadPdf(pdf.preview, pdf.name)}>
            <DocumentName>{pdf.name}</DocumentName>
            <DocumentMeta>{documentTypeLabel(pdf.name, pdf.contentType)}</DocumentMeta>
          </DocumentInfo>
          <DocumentAction
            type="button"
            aria-label={`${t('baixar')} ${pdf.name}`}
            title={t('baixar')}
            onClick={() => downloadPdf(pdf.preview, pdf.name)}
          >
            <Download1Icon size={16} color="currentColor" />
          </DocumentAction>
          {!state.feedbackInfo && (
            <DocumentAction
              type="button"
              variant="danger"
              aria-label={`${t('removerArquivo')} ${pdf.name}`}
              title={t('removerArquivo')}
              onClick={() => removePdf(index)}
            >
              <Cross1Icon width={14} height={14} />
            </DocumentAction>
          )}
        </DocumentItem>
      ))}
    </DocumentList>
  );

  return (
    <ModalWindow
      borderTop={isDesktop}
      style={{
        width: isDesktop ? '500px' : '80%',
        padding: isDesktop ? '10px' : '0px',
      }}
      onClickOutside={handleModalClose}
    >
      <ContainerModal style={{ padding: isDesktop ? '10px' : '20px' }}>
        {isLoading && (
          <Overlay>
            <Flex style={{ display: 'flex', flexDirection: 'column' }}>
              <Loader />
              {
                  state.feedbackInfo ? t('carregando') : t('enviandoInformacoes')
                }
            </Flex>
          </Overlay>
        )}
        <Flex flexDirection="column">
          {/* Header */}
          <Flex justifyContent="space-between" alignItems="center" mb="20px">
            <ModalTitle>
              {state.feedbackInfo ? t('verInformacoes') : t('adicionarInformacoes')}
            </ModalTitle>
            <Cross1Icon
              style={{ cursor: 'pointer' }}
              onClick={handleModalClose}
              color={colors.Grey400}
              width={20}
              height={20}
            />
          </Flex>

          {/* Motivo */}
          <CellModal>
            <Flex>
              <LabelModal>
                {t('porqueGostariaDeComplementarEsteLaudo?')}
                <span style={{ color: colors.Grey400 }}>
                  {` (${t('obrigatorio')})`}
                </span>
              </LabelModal>
            </Flex>
            <InputModal
              disabled={!!state.feedbackInfo}
              type="text"
              placeholder="Digite o motivo"
              value={state.reason}
              onChange={(e) => setState({ reason: e.target.value })}
            />
          </CellModal>

          {/* Telefone */}
          <br />
          <CellModal>
            <Flex>
              <LabelModal>
                {t('insiraUmTelefoneParaContato')}
                <span style={{ color: colors.Grey400 }}>
                  {` (${t('opcional')})`}
                </span>
              </LabelModal>
            </Flex>
            <InputModal
              type="tel"
              disabled={!!state.feedbackInfo}
              placeholder="(00) 00000-0000"
              value={state.phone}
              onChange={(e) => setState({ phone: formatPhone(e.target.value) })}
            />
          </CellModal>

          {/* Manutenção */}
          <br />
          <CellModal>
            <Flex>
              <LabelModal>
                {t('foiRealizadoManutencaoNoEquipamento')}
                <span style={{ color: colors.Grey400 }}>
                  {` (${t('opcional')})`}
                </span>
              </LabelModal>
            </Flex>
            <Flex>
              <RadioButton
                label={t('nao')}
                disabled={!!state.feedbackInfo}
                name="maintenance"
                style={{ marginTop: '5px' }}
                checked={maintenance === false}
                onClick={() => { !state.feedbackInfo && setMaintenance(false); }}
              />
              <RadioButton
                label={t('sim')}
                disabled={!!state.feedbackInfo}
                name="maintenance"
                style={{ marginTop: '5px', marginLeft: '20px' }}
                checked={maintenance === true}
                onClick={() => { !state.feedbackInfo && setMaintenance(true); }}
              />
            </Flex>
          </CellModal>

          {/* Data da manutenção */}
          {maintenance && (
            <>
              <br />
              <CellModal>
                <Flex>
                  <LabelModal>
                    {t('quandoFoiRealizadoAManutencaoDoEquipamento')}
                    <span style={{ color: colors.Grey400 }}>
                      {` (${t('opcional')})`}
                    </span>
                  </LabelModal>
                </Flex>
                <ContentDate>
                  <DateLabel>{t('data')}</DateLabel>
                  <SingleDatePicker
                    disabled={!!state.feedbackInfo}
                    date={state.selectedDate}
                    focused={state.isFocusedDate}
                    onFocusChange={({ focused }) => setState({ isFocusedDate: focused })}
                    onDateChange={(date) => {
                      state.selectedDate = date;
                      render();
                    }}
                    numberOfMonths={1}
                    displayFormat="DD/MM/YYYY"
                    isOutsideRange={(d) => !d.isBefore(state.tomorrow)}
                    placeholder={t('dd/mm/aaaa')}
                  />
                  <StyledCalendarIcon color={colors.brand.primary.dark} />
                </ContentDate>
              </CellModal>
            </>
          )}

          <br />
        </Flex>

        {/* Anexos */}
        {state.feedbackInfo ? (
          <>
            <AttachmentsHeader>
              <AttachmentsTitle>{t('anexos')}</AttachmentsTitle>
              {attachmentsSummary ? <AttachmentsSummary>{attachmentsSummary}</AttachmentsSummary> : null}
            </AttachmentsHeader>
            <AttachmentsPanel>
              {images.length > 0 ? (
                <div>
                  <AttachmentsSectionLabel>{t('fotos')}</AttachmentsSectionLabel>
                  <NewCarousel images={images.map((img) => img.preview)} showCount={false} />
                </div>
              ) : null}
              {pdfs.length > 0 ? (
                <div>
                  <AttachmentsSectionLabel>{t('documentos')}</AttachmentsSectionLabel>
                  {documentList}
                </div>
              ) : null}
              {images.length === 0 && pdfs.length === 0 ? (
                <AttachmentsEmpty>{t('nenhumAnexoEnviado')}</AttachmentsEmpty>
              ) : null}
            </AttachmentsPanel>
          </>
        ) : (
          <>
            <LabelModal>
              {t('adicionarImagensDasMedicoesNoEquipamento')}
              <span style={{ color: colors.Grey400 }}>
                {` (${t('opcional')})`}
              </span>
            </LabelModal>

            {images.length > 0 && (
              <>
                <br />
                <NewCarousel
                  images={images.map((img) => img.preview)}
                  showViewMode
                  onDelete={removeImage}
                />
              </>
            )}

            {pdfs.length > 0 && (
              <Flex flexDirection="column" mt="16px">
                <DocumentsCount>{`${pdfs.length} ${t(pdfs.length === 1 ? 'documentoAnexado' : 'documentosAnexados')}`}</DocumentsCount>
                {documentList}
              </Flex>
            )}

          </>
        )}

        <br />

        {/* Footer */}
        {state.feedbackInfo ? (
          <Flex
            style={{
              width: '100%',
              justifyContent: 'center',
              marginTop: '20px',
            }}
          >
            <ButtonCustom
              style={{
                backgroundColor: 'transparent',
                color: colors.Grey300,
                boxShadow: 'none',
                fontWeight: 'normal',
                border: '0px none',
              }}
              onClick={handleModalClose}
            >
              {t('fechar')}
            </ButtonCustom>
          </Flex>
        ) : (
          <>
            <ButtonAddImage
              htmlFor="imageUpload"
              style={{
                height: '41px',
                textTransform: 'capitalize',
                borderRadius: '4px',
                margin: '10px 0px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <FilePlus1Icon size={18} />
              {`${t('adicionarImagens')} / PDF`}
            </ButtonAddImage>

            <input
              id="imageUpload"
              multiple
              accept="image/*,.pdf,application/pdf"
              onChange={handleImageChange}
              style={{ display: 'none' }}
              type="file"
            />

            <Flex
              style={{
                width: '100%',
                justifyContent: 'space-between',
                marginTop: '20px',
              }}
            >
              <ButtonCustom
                style={{
                  backgroundColor: 'transparent',
                  color: colors.Grey300,
                  boxShadow: 'none',
                  fontWeight: 'normal',
                  border: '0px none',
                }}
                onClick={handleModalClose}
              >
                {t('cancelar')}
              </ButtonCustom>
              <ButtonCustom
                style={{
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={submitFeedback}
              >
                {t('enviar')}
              </ButtonCustom>
            </Flex>
          </>
        )}
      </ContainerModal>
    </ModalWindow>
  );
}
